/**
 * Offer Details Step - Displays calculated cost breakdowns for all offers
 * Shows data from the calculation engine, allows manual edits, and supports recalculation
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import {
  Calculator, ChevronDown, ChevronUp, Building2, RefreshCw,
  AlertCircle, TrendingDown, DollarSign, BarChart3, Edit3, Check, X
} from 'lucide-react';
import { useToast } from '../../../foundation/altzero_base/base/hooks/use-toast';

import { useDocumentForm } from '../contexts/DocumentFormContext';
import { usePermissions } from '../../../foundation/altzero_base/base/contextapi/PermissionsContext';
import { canEditCosts } from '../lib/costEditPermission';
import { ENERGY_RETAILERS } from '../types/offerTypes';
import {
  ContractTermBreakdown,
  OfferCostData,
  COST_CATEGORIES,
  createDefaultContractData,
  calculateContractTermTotals,
  calculateCompletionPercentage
} from '../types/offerDetailsTypes';
import { saveOfferCostBreakdowns } from '../services/offerCostBreakdownService';
import { calculateTenderCosts, calculateOfferCosts, getTenderCosts } from '../services/calculationApiService';
import { contractWeightOf } from '../services/calculationService';
import type { ContractCostBreakdown } from '../types';
import { saveOffers, getUserOrganizationId, getOffers } from '../services/tenderApiService';
import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import { mapOffersWithSites } from '../utils/siteOfferMapping';
import { formatTermLabel } from '../lib/termLabel';

type StepProps = {
  onNext: () => void;
  onBack: () => void;
};

const formatCurrency = (value: number): string => {
  return `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ─── Term-key helpers ─────────────────────────────────────────────────────────
// Backend cost_data keys are plain strings: '12', '36', '36_smooth', '36_stepped'.
// When both variants exist for the same month count we show a per-offer toggle.

function parseTermKey(key: string): { months: number; rateType: 'Smooth' | 'Stepped' | null } {
  const lower = key.toLowerCase();
  if (lower.endsWith('_smooth'))  return { months: Number(key.split('_')[0]), rateType: 'Smooth' };
  if (lower.endsWith('_stepped')) return { months: Number(key.split('_')[0]), rateType: 'Stepped' };
  return { months: parseInt(key, 10), rateType: null };
}

function getUniqueMonths(keys: string[]): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  keys.forEach(k => {
    const { months } = parseTermKey(k);
    if (!isNaN(months) && !seen.has(months)) { seen.add(months); out.push(months); }
  });
  return out.sort((a, b) => a - b);
}

function hasBothRateTypes(keys: string[]): boolean {
  const types = new Set(keys.map(k => parseTermKey(k).rateType).filter(Boolean));
  return types.has('Smooth') && types.has('Stepped');
}

function getActiveTermKey(
  keys: string[],
  months: number,
  selected: 'Smooth' | 'Stepped' | null
): string {
  const monthKeys = keys.filter(k => parseTermKey(k).months === months);
  if (monthKeys.length === 0) return String(months);
  if (monthKeys.length === 1) return monthKeys[0];
  if (!selected) return monthKeys[0];
  return monthKeys.find(k => parseTermKey(k).rateType?.toLowerCase() === selected.toLowerCase()) ?? monthKeys[0];
}
// ─────────────────────────────────────────────────────────────────────────────

const OfferDetailsStep: React.FC<StepProps> = ({ onNext, onBack }) => {
  const { sites, offers, tenderId, setOffers, setOfferDetailsData } = useDocumentForm();

  /**
   * Opening this step and rewriting the figures it shows are different rights — see
   * lib/costEditPermission.ts. Without this the step is a read-only cost review: no pencil on
   * any row, no Recalculate. A BROKER role granted Steps 5-9 could edit every quoted cost.
   */
  const { can } = usePermissions();
  const mayEditCosts = canEditCosts(can);
  const { toast } = useToast();

  // Start in loading state when tenderId is set but offers are empty (edit-mode entry)
  // to avoid a flash of "No Offers Available" before the self-heal effect runs.
  const [isLoadingOffers, setIsLoadingOffers] = useState(() => !!tenderId && offers.length === 0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [offerErrors, setOfferErrors] = useState<Record<string, string>>({});
  const [localOfferCostData, setLocalOfferCostData] = useState<Record<string, OfferCostData>>({});
  const [editingOffer, setEditingOffer] = useState<string | null>(null);
  const [editingTerm, setEditingTerm] = useState<string | null>(null);   // string key now
  const [isSaving, setIsSaving] = useState(false);
  const [rankedSelections, setRankedSelections] = useState<Array<{ rank: 1 | 2 | 3; offerId: string; termMonths: number }>>([]);

  // All raw string term keys per offer, e.g. ['12', '36_smooth', '36_stepped']
  const [offerTermKeys, setOfferTermKeys] = useState<Record<string, string[]>>({});
  // Per-offer selected rate type; only relevant when an offer has both variants
  const [selectedRateTypes, setSelectedRateTypes] = useState<Record<string, 'Smooth' | 'Stepped'>>({});

  // Load ranked_offer_selections from the tender record
  useEffect(() => {
    if (!tenderId) return;
    supabase
      .from('tender')
      .select('ranked_offer_selections')
      .eq('id', tenderId)
      .single()
      .then(({ data }) => {
        if (data?.ranked_offer_selections) {
          setRankedSelections(data.ranked_offer_selections as Array<{ rank: 1 | 2 | 3; offerId: string; termMonths: number }>);
        }
      });
  }, [tenderId]);

  // Returns 1/2/3 if this offer+term is ranked, else null
  const getRowRank = (offerId: string, termMonths: number): 1 | 2 | 3 | null => {
    const sel = rankedSelections.find(s => s.offerId === offerId && s.termMonths === termMonths);
    return sel?.rank ?? null;
  };

  // ── shared builder ────────────────────────────────────────────────────────
  function applyBreakdowns(
    offersToProcess: typeof offers,
    calculatedCosts: Map<string, { [term: string]: ContractCostBreakdown } | null>,
    prevSelected: Record<string, 'Smooth' | 'Stepped'>
  ) {
    const newLocalData: Record<string, OfferCostData> = {};
    const newTermKeys: Record<string, string[]> = {};
    const newSelected = { ...prevSelected };

    offersToProcess.forEach(offer => {
      const costsByTerm = calculatedCosts.get(offer.id);
      const contractBreakdowns: Record<string, ContractTermBreakdown> = {};
      let allKeys: string[];

      if (costsByTerm && Object.keys(costsByTerm).length > 0) {
        allKeys = Object.keys(costsByTerm);
        allKeys.forEach(key => {
          const bd = costsByTerm[key];
          // sites[*].total/energy/etc. are ANNUAL values per site.
          // The table columns (energy, environ, total) show CONTRACT totals (annual × contractWeight).
          // perYear always = annual rate = sum(sites[*].total).
          //
          // contractWeight, NOT term/12: a contract runs to the day before its anniversary, so
          // Year 1 bills 364 days. Multiplying by a plain 1/2/3/4 charged that extra day on every
          // term — $68 on a 12-month $25k tender, and the same 1/365 on 24/36/48. termWeightFor()
          // is the one definition of the factor, shared with the engine and the proposal renderer.
          //
          // The stored term_weight wins when present, for the same reason the proposal renderer
          // prefers it (scopingAi.ts's `f`): only the engine has seen the term's quoted dates,
          // and for a term not sold in whole years — AGL's "16 Months 12 Days" — those dates
          // carry a tail the month count rounded away. Recomputing here showed this table a
          // different total from the tender generated off the same row.
          const { months: termMo } = parseTermKey(key);
          const contractWeight = contractWeightOf(bd, (!isNaN(termMo) && termMo > 0) ? termMo : 12);
          const siteList = bd.sites;
          const agg = siteList && siteList.length > 0 ? {
            energy:      siteList.reduce((s, x) => s + (x.energy      || 0), 0),
            environment: siteList.reduce((s, x) => s + (x.environment || 0), 0),
            network:     siteList.reduce((s, x) => s + (x.network     || 0), 0),
            others:      siteList.reduce((s, x) => s + (x.others      || 0), 0),
            total:       siteList.reduce((s, x) => s + (x.total       || 0), 0),
          } : null;
          contractBreakdowns[key] = {
            // Contract totals: annual value × contract duration (less the Year-1 day)
            energy:      agg ? agg.energy      * contractWeight : (bd.energy      || 0),
            environment: agg ? agg.environment * contractWeight : (bd.environment || 0),
            network:     agg ? agg.network     * contractWeight : (bd.network     || 0),
            others:      agg ? agg.others      * contractWeight : (bd.others      || 0),
            total:       agg ? agg.total       * contractWeight : (bd.total       || 0),
            // perYear = annualized rate (same regardless of term length)
            perYear:     agg ? agg.total                       : (bd.perYear     || 0),
            // ⚠️ MUST be carried through, not recomputed downstream.
            //
            // This display model gets written back to offer_cost_breakdown when the workflow
            // advances (DocumentFormContext -> saveOfferCostBreakdowns). Dropping the weight
            // here made that round-trip fall back to termWeightFor(termMonths), which silently
            // replaced the engine's date-derived 1.364384 with the months-based 1.330594 —
            // leaving the money computed at the right weight but term_weight recording the
            // wrong one. The generated tender multiplies sites[] by term_weight, so the
            // document printed $18,833 where the calculation screen showed $19,311.
            term_weight: contractWeight,
            isCompleted: true,
            // Preserve per-site rate/line-item detail — the document generator's
            // Site Cost Analysis block and the rate columns in List of All Offers
            // read this directly from cost_data[term].sites[*].line_items. Without
            // it, only aggregate $ totals survive and both of those sections stay
            // blank even though the totals are correct.
            sites: siteList,
          };
        });
      } else {
        const fallback = offer.availableTerms?.length
          ? [...offer.availableTerms].sort((a, b) => a - b)
          : [12, 24, 36, 48];
        allKeys = fallback.map(String);
        allKeys.forEach(key => { contractBreakdowns[key] = createDefaultContractData(); });
      }

      newTermKeys[offer.id] = allKeys;

      // Default to Smooth when both types exist and no prior selection
      if (hasBothRateTypes(allKeys) && !newSelected[offer.id]) {
        newSelected[offer.id] = 'Smooth';
      }

      newLocalData[offer.id] = {
        offerId:          offer.id,
        offerName:        offer.retailerName || 'Unknown Retailer',
        retailerId:       offer.retailerId   || '',
        contractTerms:    getUniqueMonths(allKeys),
        contractBreakdowns,
        isComplete:       !!costsByTerm,
        lastUpdated:      new Date(),
      };
    });

    setOfferTermKeys(newTermKeys);
    setSelectedRateTypes(newSelected);
    setLocalOfferCostData(newLocalData);

    // Sync into DocumentFormContext's shared offerDetailsData — this is what
    // saveOfferCostBreakdownsToDatabase() reads from when the user proceeds to the
    // next step. Without this, only the (buggy, single-term, zero-value) data from
    // the context's own initializeOfferDetailsData() ever got persisted — the real
    // multi-term breakdowns computed here stayed local and were silently dropped,
    // leaving every non-primary contract term (and even the primary term's real
    // values) missing from the generated proposal.
    const offerCostDataArray = Object.values(newLocalData);
    setOfferDetailsData({
      offerCostData: offerCostDataArray,
      isStepComplete: offerCostDataArray.length > 0 && offerCostDataArray.every(d => d.isComplete),
      completionPercentage: calculateCompletionPercentage({
        offerCostData: offerCostDataArray,
        isStepComplete: false,
        completionPercentage: 0,
      }),
    });
  }

  // Self-heal: if offers are empty when this step mounts (e.g. edit-mode entry at step 7+),
  // load them directly from DB so the step doesn't show "no offers found".
  //
  // Also waits for `sites` before fetching (like CompareAndSelectStep already does) and
  // applies the SAME mapOffersWithSites() correction CompareAndSelectStep applies. In the
  // normal step-by-step flow, CompareAndSelectStep always runs first and already leaves
  // `offers` in context correctly mapped — this step's self-heal never even fires (offers
  // already non-empty). Edit/Regenerate jumps straight to this step and skips
  // CompareAndSelectStep entirely, so without this same correction here, per-site
  // grouping/recommended-offer logic below would silently break for exactly that entry
  // point while working fine in the normal flow.
  const hasRemappedOffersRef = useRef(false);
  useEffect(() => {
    if (offers.length > 0 || !tenderId) {
      setIsLoadingOffers(false);
      return;
    }
    // Wait for sites before fetching, same as CompareAndSelectStep. Clear the spinner while
    // waiting: sites is a dep, so this effect re-runs (and re-raises the spinner) the moment
    // they arrive — whereas bailing with isLoadingOffers still true left tenders that have
    // no sites at all stuck on "Loading offers..." forever instead of showing the empty state.
    if (sites.length === 0) {
      setIsLoadingOffers(false);
      return;
    }
    let cancelled = false;
    setIsLoadingOffers(true);
    getOffers(tenderId).then(result => {
      if (cancelled) return;
      if (result.success && result.offers && result.offers.length > 0) {
        setOffers(mapOffersWithSites(result.offers, sites));
        hasRemappedOffersRef.current = true;
      }
      setIsLoadingOffers(false);
    });
    return () => { cancelled = true; };
  }, [tenderId, sites]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net: if offers somehow loaded (from context) BEFORE sites finished loading,
  // re-map once sites become available — mirrors CompareAndSelectStep's own re-map effect.
  useEffect(() => {
    if (hasRemappedOffersRef.current) return;
    if (offers.length === 0 || sites.length === 0) return;
    hasRemappedOffersRef.current = true;

    const needsRemapping = offers.some(offer => !sites.some(s => s.id === offer.siteId));
    if (needsRemapping) {
      console.log('🔄 OfferDetailsStep: Re-mapping offers with newly loaded sites');
      setOffers(mapOffersWithSites(offers, sites));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites.length]);

  // Initialize with automatic calculation
  useEffect(() => {
    const initializeWithCalculation = async () => {
      if (offers.length === 0 || !tenderId) return;

      try {
        setIsCalculating(true);
        setCalculationError(null);

        let calculationResult = await calculateTenderCosts(tenderId);

        // If no data in DB, try syncing offers first
        if (calculationResult.calculated === 0 && offers.length > 0) {
          try {
            const { data: tenderData } = await supabase
              .from('tender')
              .select('client_id, org_id')
              .eq('id', tenderId)
              .single();

            if (tenderData) {
              const { client_id, org_id } = tenderData;
              const { siteIdMap, success: siteSuccess } = await import('../services/tenderApiService').then(m => m.saveTenderSites({
                orgId: org_id,
                tenderId: tenderId,
                sites: sites,
              }));

              if (siteSuccess && siteIdMap) {
                await import('../services/tenderApiService').then(m => m.saveOffers({
                  orgId: org_id,
                  clientId: client_id,
                  tenderId: tenderId,
                  offers: offers,
                  siteIdMap: siteIdMap
                }));
                calculationResult = await calculateTenderCosts(tenderId);
              }
            }
          } catch (syncError) {
            console.error('Auto-sync failed:', syncError);
          }
        }

        // Store per-offer errors
        const errors: Record<string, string> = {};
        if (calculationResult.errors?.length > 0) {
          calculationResult.errors.forEach(err => {
            if (err.offerId) errors[err.offerId] = err.error;
          });
        }
        setOfferErrors(errors);

        if (!calculationResult.success) {
          setCalculationError(
            `${calculationResult.failed} of ${calculationResult.calculated + calculationResult.failed} offers failed to calculate`
          );
        }

        const tenderCostsResult = await getTenderCosts(tenderId);
        const costsMap = new Map<string, { [term: string]: ContractCostBreakdown } | null>();
        if (tenderCostsResult.success && tenderCostsResult.data) {
          tenderCostsResult.data.offers.forEach(o => {
            if (o.has_calculations) {
              costsMap.set(o.offer_id, o.costs_by_term);
            }
          });
        }
        applyBreakdowns(offers, costsMap, {});
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to calculate costs';
        setCalculationError(message);
      } finally {
        setIsCalculating(false);
      }
    };

    initializeWithCalculation();
  }, [offers, tenderId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual recalculate handler
  const handleRecalculate = async () => {
    if (!tenderId || offers.length === 0) return;

    try {
      setIsCalculating(true);
      setCalculationError(null);

      const calculationResult = await calculateTenderCosts(tenderId);

      const errors: Record<string, string> = {};
      if (calculationResult.errors?.length > 0) {
        calculationResult.errors.forEach(err => {
          if (err.offerId) errors[err.offerId] = err.error;
        });
      }
      setOfferErrors(errors);

      if (!calculationResult.success) {
        setCalculationError(
          `${calculationResult.failed} of ${calculationResult.calculated + calculationResult.failed} offers failed to calculate`
        );
      }

      const tenderCostsResult = await getTenderCosts(tenderId);
      const costsMap = new Map<string, { [term: string]: ContractCostBreakdown } | null>();
      if (tenderCostsResult.success && tenderCostsResult.data) {
        tenderCostsResult.data.offers.forEach(o => {
          if (o.has_calculations) {
            costsMap.set(o.offer_id, o.costs_by_term);
          }
        });
      }
      applyBreakdowns(offers, costsMap, selectedRateTypes);

      toast({
        title: "Recalculation Complete",
        description: `Calculated ${calculationResult.calculated} offer(s) successfully.`,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to recalculate';
      setCalculationError(message);
      toast({ title: "Recalculation Failed", description: message, variant: "destructive" });
    } finally {
      setIsCalculating(false);
    }
  };

  // Update a cost field for manual editing
  const updateContractBreakdown = (
    offerId: string,
    termKey: string,           // string key e.g. '36', '36_smooth'
    field: keyof Omit<ContractTermBreakdown, 'total' | 'perYear'>,
    value: number
  ) => {
    const offerData = localOfferCostData[offerId];
    if (!offerData) return;

    const updatedBreakdowns = { ...offerData.contractBreakdowns };
    const current = updatedBreakdowns[termKey] || createDefaultContractData();
    const updated = { ...current, [field]: value };
    const { months } = parseTermKey(termKey);
    const calculated = calculateContractTermTotals(updated, isNaN(months) ? 12 : months);
    calculated.isCompleted = true;
    updatedBreakdowns[termKey] = calculated;

    setLocalOfferCostData(prev => ({
      ...prev,
      [offerId]: {
        ...offerData,
        contractBreakdowns: updatedBreakdowns,
        lastUpdated: new Date(),
      }
    }));
  };

  // Save edited offer cost data
  const handleSaveEdit = async (offerId: string) => {
    const offerData = localOfferCostData[offerId];
    if (!offerData) return;

    try {
      const result = await saveOfferCostBreakdowns(offerData);
      if (result.success) {
        toast({ title: "Saved", description: "Cost data updated successfully." });
        setEditingOffer(null);
        setEditingTerm(null);
      } else {
        toast({ title: "Save Failed", description: result.error || "Failed to save.", variant: "destructive" });
      }
    } catch {
      toast({ title: "Save Failed", description: "An error occurred while saving.", variant: "destructive" });
    }
  };

  // Calculate aggregated tender totals — memoised so it only reruns when data changes
  const { aggregatedData, recommendedTerm, contractTerms } = useMemo(() => {
    const allTerms = new Set<number>();

    Object.entries(localOfferCostData).forEach(([offId, data]) => {
      const keys = offerTermKeys[offId] || Object.keys(data.contractBreakdowns);
      getUniqueMonths(keys).forEach(m => allTerms.add(m));
    });

    if (allTerms.size === 0) {
      const offerTerms = offers.flatMap(o => o.availableTerms || []);
      const fallbackTerms = offerTerms.length > 0 ? offerTerms : [12, 24, 36, 48];
      fallbackTerms.forEach(t => allTerms.add(t));
    }

    const sortedTerms = Array.from(allTerms).sort((a, b) => a - b);
    const aggData: Record<number, {
      energy: number; environment: number; network: number; others: number;
      total: number; perYear: number; offerCount: number;
    }> = {};

    sortedTerms.forEach(term => {
      aggData[term] = { energy: 0, environment: 0, network: 0, others: 0, total: 0, perYear: Infinity, offerCount: 0 };
    });

    // Top cards show the BEST (lowest perYear) offer for each term, not the sum of all offers
    offers.forEach(offer => {
      const offerData = localOfferCostData[offer.id];
      if (!offerData) return;

      const keys = offerTermKeys[offer.id] || Object.keys(offerData.contractBreakdowns);
      const sType = selectedRateTypes[offer.id] || null;

      getUniqueMonths(keys).forEach(months => {
        const activeKey = getActiveTermKey(keys, months, sType);
        const breakdown = offerData.contractBreakdowns[activeKey];
        if (!breakdown || !aggData[months]) return;
        const offerPerYear = breakdown.perYear || 0;
        aggData[months].offerCount += 1;
        // Keep whichever offer has the lowest perYear for this term
        if (offerPerYear < aggData[months].perYear) {
          aggData[months].energy      = breakdown.energy      || 0;
          aggData[months].environment = breakdown.environment || 0;
          aggData[months].network     = breakdown.network     || 0;
          aggData[months].others      = breakdown.others      || 0;
          aggData[months].total       = breakdown.total       || 0;
          aggData[months].perYear     = offerPerYear;
        }
      });
    });

    // Reset Infinity to 0 for terms with no offers
    sortedTerms.forEach(term => {
      if (aggData[term].perYear === Infinity) aggData[term].perYear = 0;
    });

    // Use user's manually selected recommended offer term first
    const userRecOffer = offers.find(o => o.recommendedMonths && o.recommendedMonths.length > 0);
    let recTerm: number | null = userRecOffer
      ? (userRecOffer.recommendedMonths?.[0] ?? null)
      : null;

    // Fallback to lowest perYear only if no user recommendation
    if (recTerm === null) {
      let lowestPerYear = Infinity;
      Object.entries(aggData).forEach(([termStr, data]) => {
        if (data.offerCount > 0 && data.perYear < lowestPerYear) {
          lowestPerYear = data.perYear;
          recTerm = parseInt(termStr, 10);
        }
      });
    }

    // Override aggregatedData for the recommended term to show the recommended offer's costs,
    // not the cheapest offer's costs (they may be different offers).
    if (userRecOffer && recTerm !== null) {
      const recOfferData = localOfferCostData[userRecOffer.id];
      if (recOfferData) {
        const recKeys = offerTermKeys[userRecOffer.id] || Object.keys(recOfferData.contractBreakdowns);
        const recType = selectedRateTypes[userRecOffer.id] || null;
        const recActiveKey = getActiveTermKey(recKeys, recTerm, recType);
        const recBreakdown = recOfferData.contractBreakdowns[recActiveKey];
        if (recBreakdown && aggData[recTerm]) {
          aggData[recTerm] = {
            energy:      recBreakdown.energy      || 0,
            environment: recBreakdown.environment || 0,
            network:     recBreakdown.network     || 0,
            others:      recBreakdown.others      || 0,
            total:       recBreakdown.total       || 0,
            perYear:     recBreakdown.perYear     || 0,
            offerCount:  aggData[recTerm].offerCount,
          };
        }
      }
    }

    return { aggregatedData: aggData, recommendedTerm: recTerm, contractTerms: sortedTerms };
  }, [localOfferCostData, offerTermKeys, selectedRateTypes, offers]);

  // Per-site recommended-offer summary — each site can have its own recommended offer
  // (rank 1 is now site-scoped), so the top summary must show one card PER SITE instead
  // of picking just the first recommended offer found across the whole tender.
  const perSiteRecommendation = useMemo(() => {
    if (sites.length <= 1) return null;

    return sites.map(site => {
      // offers are mapOffersWithSites()-corrected, so their siteId matches site.id
      // (the tender_site junction id) — the same convention CompareAndSelectStep uses.
      const siteOffers = offers.filter(o => o.siteId === site.id);
      const recOffer = siteOffers.find(o => o.recommendedMonths && o.recommendedMonths.length > 0);

      let term: number | null = null;
      let data: { energy: number; environment: number; network: number; others: number; total: number; perYear: number } | null = null;

      if (recOffer) {
        const offerData = localOfferCostData[recOffer.id];
        if (offerData) {
          const keys = offerTermKeys[recOffer.id] || Object.keys(offerData.contractBreakdowns);
          const sType = selectedRateTypes[recOffer.id] || null;
          const recTerm = recOffer.recommendedMonths?.[0] ?? null;
          if (recTerm !== null) {
            const activeKey = getActiveTermKey(keys, recTerm, sType);
            const bd = offerData.contractBreakdowns[activeKey];
            if (bd) {
              term = recTerm;
              data = { energy: bd.energy || 0, environment: bd.environment || 0, network: bd.network || 0, others: bd.others || 0, total: bd.total || 0, perYear: bd.perYear || 0 };
            }
          }
        }
      }

      // Fallback: this site's own cheapest offer/term if none manually recommended
      if (!data) {
        let lowestPerYear = Infinity;
        siteOffers.forEach(offer => {
          const offerData = localOfferCostData[offer.id];
          if (!offerData) return;
          const keys = offerTermKeys[offer.id] || Object.keys(offerData.contractBreakdowns);
          const sType = selectedRateTypes[offer.id] || null;
          getUniqueMonths(keys).forEach(months => {
            const activeKey = getActiveTermKey(keys, months, sType);
            const bd = offerData.contractBreakdowns[activeKey];
            if (!bd) return;
            const py = bd.perYear || 0;
            if (py > 0 && py < lowestPerYear) {
              lowestPerYear = py;
              term = months;
              data = { energy: bd.energy || 0, environment: bd.environment || 0, network: bd.network || 0, others: bd.others || 0, total: bd.total || 0, perYear: py };
            }
          });
        });
      }

      return { site, term, data };
    }).filter((r): r is { site: (typeof sites)[number]; term: number; data: NonNullable<typeof r.data> } => r.data !== null && r.term !== null);
  }, [sites, offers, localOfferCostData, offerTermKeys, selectedRateTypes]);

  const formatAddress = (address: unknown): string => {
    if (!address || typeof address !== 'object') return 'Address not specified';
    const addr = address as Record<string, string>;
    const parts = [addr.street, addr.city, addr.state, addr.postcode].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Address not specified';
  };

  // Show spinner while self-healing from DB (edit-mode entry with empty context)
  if (isLoadingOffers) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-16">
          <RefreshCw className="w-12 h-12 text-blue-400 mx-auto mb-4 animate-spin" />
          <p className="text-gray-500">Loading offers...</p>
        </div>
      </div>
    );
  }

  // Empty state — shown only after load attempt completed with no offers
  if (offers.length === 0) {
    return (
      <div className="w-full p-8">
        <div className="text-center py-16">
          <Calculator className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Offers Available</h2>
          <p className="text-gray-500 mb-6">
            Please add energy offers in the previous steps before proceeding.
          </p>
          <Button onClick={onBack} variant="outline">Go Back to Add Offers</Button>
        </div>
      </div>
    );
  }

  const hasCalculatedData = Object.values(aggregatedData).some(d => d.offerCount > 0);

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-blue-600" />
            Cost Analysis
          </h1>
          <p className="text-gray-500 mt-1">
            Calculated cost breakdowns for {offers.length} offer{offers.length !== 1 ? 's' : ''} across {sites.length} site{sites.length !== 1 ? 's' : ''}
          </p>
        </div>
        {!isCalculating && mayEditCosts && (
          <Button onClick={handleRecalculate} variant="outline" className="gap-2" disabled={offers.length === 0}>
            <RefreshCw className="w-4 h-4" />
            Recalculate
          </Button>
        )}
      </div>

      {/* Loading State */}
      {isCalculating && (
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <h3 className="text-lg font-semibold text-gray-900">Calculating Costs...</h3>
              <p className="text-gray-500 text-sm">
                Processing {offers.length} offer{offers.length !== 1 ? 's' : ''} across all contract terms
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calculation Warning */}
      {calculationError && !isCalculating && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-amber-900">{calculationError}</p>
            <p className="text-amber-700 text-sm mt-1">You can review available data below or edit costs manually.</p>
          </div>
        </div>
      )}

      {/* Tender Summary Cards — one per site when multi-site, else one tender-wide card */}
      {!isCalculating && hasCalculatedData && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {perSiteRecommendation ? (
            perSiteRecommendation.map(({ site, term, data }) => (
              <Card key={site.id} className="relative overflow-hidden border-green-400 bg-green-50/50 ring-2 ring-green-300">
                <div className="absolute top-0 right-0 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-md">
                  RECOMMENDED
                </div>
                <CardContent className="pt-5 pb-4 px-4">
                  <div className="text-xs font-semibold text-blue-900 flex items-center gap-1 mb-1.5 truncate">
                    <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                    {site.name}
                  </div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                    {term}-Month Term
                  </div>
                  <div className="text-xl font-bold text-gray-900">
                    {formatCurrency(data.perYear)}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">per year avg</div>
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="text-sm font-medium text-gray-700">
                      {formatCurrency(data.total)}
                    </div>
                    <div className="text-xs text-gray-400">total contract cost</div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            contractTerms.filter(term => aggregatedData[term]?.offerCount > 0 && term === recommendedTerm).map(term => {
              const data = aggregatedData[term];
              const isRecommended = term === recommendedTerm;
              return (
                <Card key={term} className={`relative overflow-hidden ${isRecommended ? 'border-green-400 bg-green-50/50 ring-2 ring-green-300' : 'border-gray-200'}`}>
                  {isRecommended && (
                    <div className="absolute top-0 right-0 bg-green-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-bl-md">
                      RECOMMENDED
                    </div>
                  )}
                  <CardContent className="pt-5 pb-4 px-4">
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      {term}-Month Term
                    </div>
                    <div className="text-xl font-bold text-gray-900">
                      {formatCurrency(data.perYear)}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">per year avg</div>
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-sm font-medium text-gray-700">
                        {formatCurrency(data.total)}
                      </div>
                      <div className="text-xs text-gray-400">total contract cost</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Detailed Cost Breakdown Table */}
      {!isCalculating && hasCalculatedData && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-blue-600" />
              Detailed Cost Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/80">
                    <th className="text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Retailer / Term</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Energy</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Environmental</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Network</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Other</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Per Year</th>
                    <th className="text-center py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(sites.length > 1
                    ? sites
                        // offers are mapOffersWithSites()-corrected, so their siteId matches
                        // site.id (the tender_site junction id) — same convention used
                        // everywhere else (CompareAndSelectStep, Breakdown by Site below).
                        .map(s => ({ site: s, siteOffers: offers.filter(o => o.siteId === s.id) }))
                        .filter(g => g.siteOffers.length > 0)
                    : [{ site: null as (typeof sites)[number] | null, siteOffers: offers }]
                  ).map(({ site, siteOffers }) => (
                  <React.Fragment key={site?.id || 'all-sites'}>
                    {site && (
                      <tr className="bg-blue-50/70 border-y border-blue-100">
                        <td colSpan={8} className="py-2 px-4">
                          <div className="flex items-center gap-2 text-sm font-semibold text-blue-900">
                            <Building2 className="w-4 h-4" />
                            {site.name}{site.nmi ? ` — NMI: ${site.nmi}` : ''}
                          </div>
                        </td>
                      </tr>
                    )}
                  {siteOffers.map((offer) => {
                    const retailer = ENERGY_RETAILERS.find(r => r.id === offer.retailerId);
                    const retailerName = retailer?.name || offer.retailerName || 'Unknown Retailer';
                    const offerData = localOfferCostData[offer.id];
                    const termKeys = offerTermKeys[offer.id] || (offerData ? Object.keys(offerData.contractBreakdowns) : []);
                    const uniqueMonths = offerData?.contractTerms || (offer.availableTerms?.length ? offer.availableTerms : []);
                    const selectedType = selectedRateTypes[offer.id] || null;
                    const offerHasBothTypes = hasBothRateTypes(termKeys);
                    const hasError = offerErrors[offer.id];
                    const isEditing = editingOffer === offer.id;

                    return (
                      <React.Fragment key={offer.id}>
                        {/* Per-offer error row */}
                        {hasError && (
                          <tr className="bg-red-50">
                            <td colSpan={8} className="px-4 py-2">
                              <div className="flex items-center gap-2 text-sm text-red-700">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                <span className="font-medium">{retailerName}:</span>
                                <span>
                                  {hasError.includes('<!DOCTYPE') || hasError.includes('<html') || hasError.includes('<title>')
                                    ? 'Calculation failed — database connection error. Please try again.'
                                    : hasError.length > 200 ? `${hasError.slice(0, 200)}…` : hasError}
                                </span>
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Contract term rows */}
                        {uniqueMonths.map((termMonths, termIndex) => {
                          if (!offerData) return null;

                          // Resolve which string key to use for this month count
                          const activeKey = getActiveTermKey(termKeys, termMonths, selectedType);
                          const contractData = offerData.contractBreakdowns[activeKey] || createDefaultContractData();
                          // Permission is re-checked at render, not just where the pencil is drawn:
                          // an edit already in flight must close if the right goes away mid-session.
                          const isEditingThisTerm = mayEditCosts && isEditing && editingTerm === activeKey;
                          const isFirstRow = termIndex === 0;
                          const rowRateType = parseTermKey(activeKey).rateType;

                          const rowRank = getRowRank(offer.id, termMonths);

                          const isLowest = !rowRank && contractData.perYear > 0 &&
                            !offers.some(o => {
                              const d = localOfferCostData[o.id];
                              if (!d) return false;
                              const dKeys = offerTermKeys[o.id] || Object.keys(d.contractBreakdowns);
                              const dKey = getActiveTermKey(dKeys, termMonths, selectedRateTypes[o.id] || null);
                              return d.contractBreakdowns[dKey]?.perYear > 0 &&
                                     d.contractBreakdowns[dKey]?.perYear < contractData.perYear;
                            });

                          return (
                            <tr key={`${offer.id}-${termMonths}`} className={[
                              rowRank === 1 ? 'bg-green-50/60' : rowRank === 2 ? 'bg-blue-50/60' : rowRank === 3 ? 'bg-purple-50/60' : isFirstRow ? '' : 'bg-gray-50/30',
                              'hover:bg-blue-50/30 transition-colors',
                            ].join(' ')}>
                              <td className="py-3 px-4">
                                <div className="flex items-center gap-3">
                                  {isFirstRow && (
                                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                                      <span className="text-white font-bold text-xs">
                                        {retailerName.charAt(0)}
                                      </span>
                                    </div>
                                  )}
                                  {!isFirstRow && <div className="w-8 h-8 flex-shrink-0" />}
                                  <div>
                                    {isFirstRow && (
                                      <div className="font-semibold text-gray-900 text-sm">{retailerName}</div>
                                    )}

                                    {/* ── Stepped / Smooth toggle — only when both variants exist ── */}
                                    {isFirstRow && offerHasBothTypes && (
                                      <div className="flex items-center gap-1 mt-0.5 mb-1">
                                        {(['Smooth', 'Stepped'] as const).map(type => (
                                          <button
                                            key={type}
                                            onClick={() => setSelectedRateTypes(prev => ({ ...prev, [offer.id]: type }))}
                                            className={[
                                              'text-[10px] font-semibold px-2.5 py-0.5 rounded-full border transition-all',
                                              (selectedRateTypes[offer.id] ?? 'Smooth') === type
                                                ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                                                : 'bg-white text-gray-500 border-gray-300 hover:border-blue-400 hover:text-blue-600',
                                            ].join(' ')}
                                          >
                                            {type}
                                          </button>
                                        ))}
                                      </div>
                                    )}

                                    <div className="text-xs text-gray-500 flex items-center gap-1.5">
                                      {formatTermLabel(termMonths)}

                                      {/* Subtle badge when only one rate type exists */}
                                      {rowRateType && !offerHasBothTypes && (
                                        <span className={[
                                          'text-[10px] px-1.5 rounded-sm font-medium',
                                          rowRateType === 'Smooth'
                                            ? 'bg-blue-50 text-blue-600'
                                            : 'bg-purple-50 text-purple-600',
                                        ].join(' ')}>
                                          {rowRateType}
                                        </span>
                                      )}

                                      {rowRank === 1 && (
                                        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-green-600 text-white">★ 1st</Badge>
                                      )}
                                      {rowRank === 2 && (
                                        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-blue-600 text-white">2nd</Badge>
                                      )}
                                      {rowRank === 3 && (
                                        <Badge className="text-[10px] px-1.5 py-0 h-4 bg-purple-600 text-white">3rd</Badge>
                                      )}
                                      {isLowest && (
                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-green-300 text-green-700 bg-green-50">
                                          <TrendingDown className="w-2.5 h-2.5 mr-0.5" />
                                          Lowest
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {isEditingThisTerm ? (
                                <>
                                  {(['energy', 'environment', 'network', 'others'] as const).map(field => (
                                    <td key={field} className="py-2 px-2">
                                      <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={contractData[field] || ''}
                                        onChange={(e) => {
                                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                          if (!isNaN(val)) updateContractBreakdown(offer.id, activeKey, field, val);
                                        }}
                                        className="text-right text-sm h-8 w-28"
                                      />
                                    </td>
                                  ))}
                                  <td className="py-3 px-4 text-right font-semibold text-sm text-gray-900">{formatCurrency(contractData.total)}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-sm text-gray-900">{formatCurrency(contractData.perYear)}</td>
                                  <td className="py-2 px-4 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handleSaveEdit(offer.id)}>
                                        <Check className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-gray-600" onClick={() => { setEditingOffer(null); setEditingTerm(null); }}>
                                        <X className="w-3.5 h-3.5" />
                                      </Button>
                                    </div>
                                  </td>
                                </>
                              ) : (
                                <>
                                  <td className="py-3 px-4 text-right text-sm text-gray-700 tabular-nums">{formatCurrency(contractData.energy)}</td>
                                  <td className="py-3 px-4 text-right text-sm text-gray-700 tabular-nums">{formatCurrency(contractData.environment)}</td>
                                  <td className="py-3 px-4 text-right text-sm text-gray-700 tabular-nums">{formatCurrency(contractData.network)}</td>
                                  <td className="py-3 px-4 text-right text-sm text-gray-700 tabular-nums">{formatCurrency(contractData.others)}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-sm text-gray-900 tabular-nums">{formatCurrency(contractData.total)}</td>
                                  <td className="py-3 px-4 text-right font-semibold text-sm text-gray-900 tabular-nums">{formatCurrency(contractData.perYear)}</td>
                                  <td className="py-3 px-4 text-center">
                                    {mayEditCosts && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 w-7 p-0 text-gray-400 hover:text-blue-600"
                                        onClick={() => { setEditingOffer(offer.id); setEditingTerm(activeKey); }}
                                      >
                                        <Edit3 className="w-3.5 h-3.5" />
                                      </Button>
                                    )}
                                  </td>
                                </>
                              )}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                  </React.Fragment>
                  ))}

                  {/* Aggregated Totals Row */}
                  {Object.entries(aggregatedData).filter(([termStr, data]) => data.offerCount > 0 && parseInt(termStr) === recommendedTerm).map(([termStr, data]) => {
                    const termMonths = parseInt(termStr);
                    const isRecommended = true;
                    return (
                      <tr key={`total-${termMonths}`} className={`border-t-2 border-gray-300 font-semibold ${isRecommended ? 'bg-green-50' : 'bg-gray-50'}`}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="text-sm text-gray-700">Total ({formatTermLabel(termMonths)})</div>
                            {isRecommended && (
                              <Badge className="bg-green-600 text-white text-[10px] px-1.5 py-0 h-4">Recommended</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">{formatCurrency(data.energy)}</td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">{formatCurrency(data.environment)}</td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">{formatCurrency(data.network)}</td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">{formatCurrency(data.others)}</td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">{formatCurrency(data.total)}</td>
                        <td className="py-3 px-4 text-right text-sm tabular-nums">{formatCurrency(data.perYear)}</td>
                        <td className="py-3 px-4"></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Site Breakdown */}
      {!isCalculating && hasCalculatedData && sites.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5 text-gray-600" />
              Breakdown by Site
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sites.map(site => {
                // offers are mapOffersWithSites()-corrected to the same site.id
                // (junction id) convention CompareAndSelectStep uses.
                const siteOffers = offers.filter(o => o.siteId === site.id);
                if (siteOffers.length === 0) return null;

                return (
                  <div key={site.id} className="border rounded-lg p-4 bg-gray-50/50">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="w-4 h-4 text-gray-500" />
                      <div>
                        <h4 className="font-medium text-sm text-gray-900">{site.name}</h4>
                        <p className="text-xs text-gray-500">{formatAddress(site.address)}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {siteOffers.map(offer => {
                        const retailer = ENERGY_RETAILERS.find(r => r.id === offer.retailerId);
                        const offerData = localOfferCostData[offer.id];
                        if (!offerData) return null;

                        const keys = offerTermKeys[offer.id] || Object.keys(offerData.contractBreakdowns);
                        const sType = selectedRateTypes[offer.id] || null;

                        const bestTerm = getUniqueMonths(keys)
                          .map(months => {
                            const k = getActiveTermKey(keys, months, sType);
                            return { months, key: k, breakdown: offerData.contractBreakdowns[k] };
                          })
                          .filter(({ breakdown }) => breakdown?.perYear > 0)
                          .sort((a, b) => a.breakdown.perYear - b.breakdown.perYear)[0];

                        return (
                          <div key={offer.id} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{retailer?.name || offer.retailerName}</span>
                            {bestTerm && (
                              <span className="font-medium text-gray-900">
                                {formatCurrency(bestTerm.breakdown.perYear)}/yr
                                <span className="text-xs text-gray-400 ml-1">({formatTermLabel(bestTerm.months)})</span>
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recommendation */}
      {!isCalculating && recommendedTerm && hasCalculatedData && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 border border-green-200">
          <TrendingDown className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-green-900">
              Recommended: {recommendedTerm}-month contract term
            </p>
            <p className="text-green-700 text-sm mt-1">
              Offers the lowest average annual cost at {formatCurrency(aggregatedData[recommendedTerm]?.perYear || 0)}/year across all offers.
            </p>
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t border-gray-200">
        <Button onClick={onBack} variant="outline">Previous Step</Button>
        <Button
          onClick={() => onNext()}
          className="bg-blue-600 hover:bg-blue-700"
          disabled={isCalculating}
        >
          Continue
        </Button>
      </div>
    </div>
  );
};

export default OfferDetailsStep;
